import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../auth/AuthProvider";
import { fetchWithAuth } from "../api/http";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CustomerResult {
  customer_id: string;
  customer_name: string;
}

interface ProjectResult {
  project_id: string;
  project_name: string;
  tech_type_id?: string | null;
  latest_run_status?: string | null;
  eia_ba_code?: string | null;
}

interface SearchItem {
  id: string;
  type: "customer" | "project";
  name: string;
  subtitle: string;
  status?: string | null;
  techType?: string | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function techLabel(techType?: string | null): string {
  if (!techType) return "";
  const map: Record<string, string> = {
    solar_ground: "Solar",
    solar_rooftop: "Solar Rooftop",
    wind_onshore: "Wind",
    wind_offshore: "Offshore Wind",
  };
  return map[techType] ?? techType;
}

function statusColor(status?: string | null): string {
  switch (status) {
    case "completed":
      return "bg-emerald-500/20 text-emerald-400";
    case "running":
      return "bg-blue-500/20 text-blue-400";
    case "queued":
      return "bg-amber-500/20 text-amber-400";
    case "failed":
      return "bg-red-500/20 text-red-400";
    default:
      return "bg-surface-600 text-text-muted";
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { token, isAdmin } = useAuth();

  // ---- Keyboard shortcut: Cmd+K / Ctrl+K to open ----
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  // Focus input when modal opens; reset state on close
  useEffect(() => {
    if (open) {
      // Slight delay so the DOM is ready
      requestAnimationFrame(() => inputRef.current?.focus());
    } else {
      setQuery("");
      setSelectedIndex(0);
    }
  }, [open]);

  // ---- Data fetching ----
  const { data: customers = [] } = useQuery<CustomerResult[]>({
    queryKey: ["global-search", "customers"],
    queryFn: () => fetchWithAuth<CustomerResult[]>("/admin/customers", token!),
    enabled: open && isAdmin && !!token,
    staleTime: 60_000,
  });

  const { data: projects = [] } = useQuery<ProjectResult[]>({
    queryKey: ["global-search", "projects"],
    queryFn: () => fetchWithAuth<ProjectResult[]>("/projects", token!),
    enabled: open && !!token,
    staleTime: 60_000,
  });

  // ---- Filter & group ----
  const filtered = useMemo<SearchItem[]>(() => {
    const q = query.toLowerCase().trim();
    const items: SearchItem[] = [];

    // Customers (admin only)
    if (isAdmin) {
      for (const c of customers) {
        if (!q || c.customer_name.toLowerCase().includes(q)) {
          items.push({
            id: c.customer_id,
            type: "customer",
            name: c.customer_name,
            subtitle: "Customer",
          });
        }
      }
    }

    // Projects
    for (const p of projects) {
      if (!q || p.project_name.toLowerCase().includes(q)) {
        const tech = techLabel(p.tech_type_id);
        const parts = [tech, p.eia_ba_code].filter(Boolean);
        items.push({
          id: p.project_id,
          type: "project",
          name: p.project_name,
          subtitle: parts.length ? parts.join(" \u00b7 ") : "Project",
          status: p.latest_run_status,
          techType: p.tech_type_id,
        });
      }
    }

    return items;
  }, [query, customers, projects, isAdmin]);

  // Group items for rendering (with section headers)
  const groups = useMemo(() => {
    const customerItems = filtered.filter((i) => i.type === "customer");
    const projectItems = filtered.filter((i) => i.type === "project");
    return { customerItems, projectItems };
  }, [filtered]);

  // Flat list for keyboard nav
  const flatList = useMemo(
    () => [...groups.customerItems, ...groups.projectItems],
    [groups]
  );

  // Clamp selected index
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  // ---- Navigation ----
  const navigateTo = useCallback(
    (item: SearchItem) => {
      setOpen(false);
      if (item.type === "customer") {
        navigate(`/admin/clients/${item.id}`);
      } else {
        navigate(
          isAdmin
            ? `/admin/projects/${item.id}`
            : `/app/projects/${item.id}`
        );
      }
    },
    [navigate, isAdmin]
  );

  // ---- Keyboard nav inside modal ----
  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Escape") {
      setOpen(false);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((i) => Math.min(i + 1, flatList.length - 1));
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((i) => Math.max(i - 1, 0));
    }
    if (e.key === "Enter" && flatList[selectedIndex]) {
      e.preventDefault();
      navigateTo(flatList[selectedIndex]);
    }
  }

  // Keep selected item scrolled into view
  useEffect(() => {
    const container = listRef.current;
    if (!container) return;
    const el = container.querySelector(`[data-idx="${selectedIndex}"]`);
    if (el) {
      (el as HTMLElement).scrollIntoView({ block: "nearest" });
    }
  }, [selectedIndex]);

  if (!open) return null;

  // ---- Render ----
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh] bg-black/60 backdrop-blur-sm"
      onClick={() => setOpen(false)}
    >
      <div
        className="w-full max-w-xl rounded-xl bg-surface-800 border border-border shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
          <SearchIcon className="h-5 w-5 text-text-muted shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search projects and customers..."
            className="flex-1 bg-transparent text-text-primary placeholder-text-muted text-body-sm outline-none"
          />
          <kbd className="hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-surface-700 text-[11px] text-text-muted border border-border font-mono">
            ESC
          </kbd>
        </div>

        {/* Results */}
        <div ref={listRef} className="max-h-[50vh] overflow-y-auto p-2">
          {flatList.length === 0 && query.length > 0 && (
            <p className="px-3 py-6 text-center text-body-sm text-text-muted">
              No results for &ldquo;{query}&rdquo;
            </p>
          )}

          {flatList.length === 0 && query.length === 0 && (
            <p className="px-3 py-6 text-center text-body-sm text-text-muted">
              Type to search across projects{isAdmin ? " and customers" : ""}...
            </p>
          )}

          {/* Customer group */}
          {groups.customerItems.length > 0 && (
            <>
              <div className="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-text-muted">
                Customers
              </div>
              {groups.customerItems.map((item) => {
                const idx = flatList.indexOf(item);
                const isSelected = idx === selectedIndex;
                return (
                  <button
                    key={item.id}
                    data-idx={idx}
                    onClick={() => navigateTo(item)}
                    onMouseEnter={() => setSelectedIndex(idx)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                      isSelected
                        ? "bg-quantum-500/10 border-l-2 border-quantum-400"
                        : "hover:bg-surface-700/60 border-l-2 border-transparent"
                    }`}
                  >
                    <BuildingIcon className="h-4 w-4 text-text-muted shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-body-sm text-text-primary truncate">
                        {item.name}
                      </div>
                      <div className="text-[12px] text-text-muted truncate">
                        {item.subtitle}
                      </div>
                    </div>
                  </button>
                );
              })}
            </>
          )}

          {/* Project group */}
          {groups.projectItems.length > 0 && (
            <>
              <div className="px-3 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-text-muted">
                Projects
              </div>
              {groups.projectItems.map((item) => {
                const idx = flatList.indexOf(item);
                const isSelected = idx === selectedIndex;
                return (
                  <button
                    key={item.id}
                    data-idx={idx}
                    onClick={() => navigateTo(item)}
                    onMouseEnter={() => setSelectedIndex(idx)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                      isSelected
                        ? "bg-quantum-500/10 border-l-2 border-quantum-400"
                        : "hover:bg-surface-700/60 border-l-2 border-transparent"
                    }`}
                  >
                    <TechIcon
                      techType={item.techType}
                      className="h-4 w-4 text-text-muted shrink-0"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="text-body-sm text-text-primary truncate">
                        {item.name}
                      </div>
                      <div className="text-[12px] text-text-muted truncate">
                        {item.subtitle}
                      </div>
                    </div>
                    {item.status && (
                      <span
                        className={`shrink-0 px-2 py-0.5 rounded text-[11px] font-medium ${statusColor(
                          item.status
                        )}`}
                      >
                        {item.status}
                      </span>
                    )}
                  </button>
                );
              })}
            </>
          )}
        </div>

        {/* Footer hint */}
        <div className="flex items-center gap-4 px-4 py-2 border-t border-border text-[11px] text-text-muted">
          <span className="flex items-center gap-1">
            <kbd className="px-1 py-0.5 rounded bg-surface-700 border border-border font-mono">&uarr;</kbd>
            <kbd className="px-1 py-0.5 rounded bg-surface-700 border border-border font-mono">&darr;</kbd>
            navigate
          </span>
          <span className="flex items-center gap-1">
            <kbd className="px-1 py-0.5 rounded bg-surface-700 border border-border font-mono">&crarr;</kbd>
            open
          </span>
          <span className="flex items-center gap-1">
            <kbd className="px-1 py-0.5 rounded bg-surface-700 border border-border font-mono">esc</kbd>
            close
          </span>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
      />
    </svg>
  );
}

function BuildingIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
      />
    </svg>
  );
}

function SolarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
      />
    </svg>
  );
}

function WindIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9.59 4.59A2 2 0 1111 8H2m10.59 11.41A2 2 0 1014 16H2m15.73-8.27A2.5 2.5 0 1119.5 12H2"
      />
    </svg>
  );
}

function TechIcon({
  techType,
  className,
}: {
  techType?: string | null;
  className?: string;
}) {
  if (techType?.startsWith("wind")) return <WindIcon className={className} />;
  return <SolarIcon className={className} />;
}
